// Domain 03 check helpers — read live (cloned) routing state; never mutate the lab.
import { converge } from '../../engine/net/routing.js';
import { routingTable, roundTrip, trace } from '../../engine/net/sim.js';
import { clone } from '../../engine/net/model.js';
import { inSubnet } from '../../engine/net/addr.js';
export * from '../d01/helpers.js';

const C = (rt) => converge(clone(rt.net));
export const rib = (rt, id) => routingTable(clone(rt.net), id);
/** Best route(s) a router would use for an address (longest prefix). */
export function bestRoute(rt, id, ip) {
  const r = rib(rt, id).filter((x) => x.code !== 'L' && inSubnet(ip, x.net, x.prefix));
  if (!r.length) return null;
  const p = Math.max(...r.map((x) => x.prefix));
  return r.filter((x) => x.prefix === p);
}
export const hasRoute = (rt, id, net, prefix, pred = () => true) => rib(rt, id).some((r) => r.net === net && r.prefix === prefix && pred(r));
export const routeVia = (rt, id, net, prefix, src) => hasRoute(rt, id, net, prefix, (r) => r.src === src);
export const defaultVia = (rt, id, nh) => rib(rt, id).some((r) => r.prefix === 0 && (!nh || r.nh === nh));

export function ospfNbr(rt, a, b, area = 0) {
  const st = Object.entries(C(rt).ospf.state).find(([k]) => k.startsWith(`${a}|`))?.[1];
  const ridB = Object.entries(C(rt).ospf.state).find(([k]) => k.startsWith(`${b}|`))?.[1]?.rid;
  return !!st && !!ridB && st.neighbors.some((n) => n.rid === ridB && String(n.area) === String(area));
}
export const ospfRid = (rt, id) => Object.entries(C(rt).ospf.state).find(([k]) => k.startsWith(`${id}|`))?.[1]?.rid || null;
export function eigrpNbr(rt, a, b, asn = '100') {
  const c = C(rt);
  const st = c.eigrp.state[`${a}|${asn}`];
  const other = c.eigrp.state[`${b}|${asn}`];
  if (!st || !other) return false;
  const ips = other.ifs.map((x) => x.ip);
  return st.neighbors.some((n) => ips.includes(n.ip));
}
export const bgpState = (rt, id, ip) => C(rt).bgp.state[id]?.neighbors?.[ip]?.state || null;
export const ripVersionOk = (rt, ids) => ids.every((id) => rt.net.devices[id].rip?.version === 2);
/** End-to-end reachability without loops (traceroute must finish). */
export const reachTrace = (rt, src, dst) => {
  const n = clone(rt.net);
  if (!roundTrip(n, src, dst).ok) return false;
  const t = trace(clone(rt.net), src, dst, 12);
  return !!t.at(-1)?.final;
};
/** First router hop IPs of a traceroute (for "path goes via X" checks). */
export const tracePath = (rt, src, dst) => trace(clone(rt.net), src, dst, 12).map((h) => h.ip);
export const iosRoute = (device, re) => (ev) => ev.type === 'cmd' && ev.device === device && /^show ip route/.test(ev.key) && (!re || re.test(ev.output));
export const iosTrace = (device, target, re) => (ev) => ev.type === 'cmd' && ev.device === device && ev.key === 'traceroute <HOST>' && ev.args?.[0] === target && (!re || re.test(ev.output));
